// External libs
import React from "react";
import PropTypes from "prop-types";
import { ResponsiveLine } from "@nivo/line";
import { prop, first } from "lodash/fp";

const getYScaleMin = (marginPercentage = 0.05) => (data) => {
  if (!data?.length || !data[0]?.data?.length) return 0;
  const yValues = first(data).data.map(prop("y"));
  const minValue = Math.min(...yValues);
  const maxValue = Math.max(...data.flatMap(d => d.data.map(p => p.y)));
  const range = maxValue - minValue;
  const margin = range * marginPercentage;
  return minValue - margin;
};

/**
 * A responsive line chart component that displays data over time. Uses the Nivo
 * line graph [https://nivo.rocks/line/api/].
 * 
 * @param {Array} data - An array of data objects to render in the line chart.
 * Each data object should have an `id`, `data` (array of time-value pairs), and optionally `yAxis` for secondary axis usage.
 * 
 * @returns {JSX.Element} The rendered line chart with optional axes and legends.
 * 
 * @example
 * const data = [
 *   {
 *     id: "sensor1",
 *     data: [{ x: "2025-02-18T00:00:00", y: 20 }, { x: "2025-02-18T01:00:00", y: 25 }],
 *   },
 *   {
 *     id: "sensor2",
 *     data: [{ x: "2025-02-18T00:00:00", y: 40 }, { x: "2025-02-18T01:00:00", y: 45 }],
 *     yAxis: "right",
 *   }
 * ];
 * <WidgetGraphline data={data} />
 */
const WidgetGraphline = ({ data }) => {

  /**
   * Gets the string to be displayed on the axis and the legend for this
   * particular data point
   * @param {object} data single data point
   * @returns string which shows the type of data
   */
  const getAxisLegend = (data) => {
    return data?.id || "N/A";
  };

  return (
    <div style={{ height: 350 }}>
      <ResponsiveLine
        data={data}
        margin={{ top: 10, right: 50, bottom: 75, left: 50 }}
        xScale={{ type: "time", format: "native" }}
        xFormat="time:%H:%M:%S"
        yScale={{ type: "linear",
          min: data?.length ? getYScaleMin(0.05)(data) : "auto",
          max: 'auto',
         }}
        axisLeft={{
          legend: getAxisLegend(data[0]),
          legendOffset: -40,
          legendPosition: "middle",
        }}
        axisRight={
          data.some((d) => d.yAxis === "right")
            ? {
                legend: getAxisLegend(data[1]),
                legendOffset: 40,
                legendPosition: "middle",
              }
            : null
        }
        axisBottom={{
          format: "%H:%M:%S",
          legend: "Time",
          legendOffset: 40,
          legendPosition: "middle",
          tickRotation: -20,
        }}
        colors={["#2d22b2", "#7522b2", "#225fb2"]}
        colorBy="index"
        pointSize={6}
        pointColor={{ from: 'color', modifiers: [] }}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'color', modifiers: [] }}
        enableArea={true}
        areaBaselineValue={getYScaleMin(0.05)(data) }
        areaOpacity={0.1}
        useMesh={true}
        legends={[
          {
            anchor: "bottom",
            direction: "row",
            justify: false,
            translateX: 0,
            translateY: 75,
            itemsSpacing: 50,
            itemDirection: "left-to-right",
            itemWidth: 80,
            itemHeight: 20,
            itemOpacity: 0.75,
            symbolSize: 12,
            symbolShape: "circle",
          },
        ]}
      />
    </div>
  );
};

WidgetGraphline.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default WidgetGraphline;
